import { Color3, MeshBuilder, StandardMaterial, type Mesh, type Scene } from "@babylonjs/core";
import { Btn, HeroId, Slot, fxk, hv, pk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import { brass, click, drive, fm, later, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import { explosion, fire } from "../audio-cues.ts";
import type { ClientHero, GearCtx, ProjectileFrame } from "../types.ts";

const V = { fuel: 0, overdrive: 1, beamTarget: 2, ultGlow: 3 } as const;
const PK = { Repulsor: pk(HeroId.IonMan, 0), Missile: pk(HeroId.IonMan, 1), Pulse: pk(HeroId.IonMan, 2) } as const;
const FX = { Repulsor: fxk(HeroId.IonMan, 0), Beam: fxk(HeroId.IonMan, 1), Pulse: fxk(HeroId.IonMan, 2) } as const;

const theme = {
  bpm: 132,
  octave: 0,
  voice: brass,
  notes: [
    [0, 0.5],
    [4, 0.5],
    [7, 0.5],
    [16, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

const ionFlight: Cue = trim(5.5, (o) => {
  thump(o, 0.45, 90, 42, 0.18);
  whoosh(o, 0.55, 420, 2600, 0.45);
  noise(o, { color: "crackle", type: "bandpass", f: 2300, gain: 0.25, d: 0.24 });
  return 0.6;
});
const ionRedline: Cue = trim(5.8, (o) => {
  fm(drive(o, 1.7), { f: 180, ratio: 2.02, index: 5, index1: 0.6, gain: 0.14, d: 0.32 });
  [0, 7, 16].forEach((s, i) => tone(later(o, i * 0.04), { type: "sawtooth", f: 220 * 2 ** (s / 12), gain: 0.035, d: 0.22, lp: 2400 }));
  click(o, 0.22, 4200, 0.004);
  return 0.55;
});
const ionMissiles: Cue = trim(3, (o) => {
  for (let i = 0; i < 6; i++) {
    whoosh(later(o, i * 0.028), 0.18, 1100, 3600, 0.12);
    click(later(o, i * 0.028), 0.08, 3600, 0.003);
  }
  thump(o, 0.3, 130, 70, 0.16);
  return 0.42;
});
const ionPulse: Cue = trim(-5, (o) => {
  thump(o, 0.75, 78, 28, 0.55);
  fm(drive(o, 2), { f: 70, ratio: 1.5, index: 7, index1: 1.2, gain: 0.2, a: 0.05, d: 0.55 });
  noise(o, { color: "brown", type: "lowpass", f: 900, gain: 0.35, a: 0.08, d: 0.65 });
  return 1.0;
});
const ionReload: Cue = trim(15, (o) => {
  click(o, 0.18, 1600, 0.008);
  whoosh(later(o, 0.18), 0.16, 500, 1100, 0.24);
  click(later(o, 0.62), 0.2, 2600, 0.006);
  fm(later(o, 0.72), { f: 420, ratio: 2, index: 1.8, index1: 0, gain: 0.06, d: 0.14 });
  return 1.4;
});

type Gear = {
  chest: Mesh;
  beam: Mesh;
  jetFlames: Mesh[];
  glowParts: Mesh[];
  shoulderL: Mesh;
  shoulderR: Mesh;
};

function cylinderBeam(g: GearCtx) {
  const beam = g.addWorld(MeshBuilder.CreateCylinder("ion-unibeam", { diameter: 0.18, height: 1, tessellation: 18 }, g.scene), g.glow("#b9fbff", 0.82));
  beam.rotation.x = Math.PI / 2;
  beam.setEnabled(false);
  return beam;
}

function mat(scene: Scene, color: string, glow = false) {
  const m = new StandardMaterial(`mat-${color}`, scene);
  m.diffuseColor = Color3.FromHexString(color);
  if (glow) m.emissiveColor = Color3.FromHexString(color);
  return m;
}

function projectile(scene: Scene, f: ProjectileFrame, color: string, size: number) {
  const body = MeshBuilder.CreateSphere("ion-shot", { diameter: size, segments: 12 }, scene);
  const halo = MeshBuilder.CreateTorus("ion-shot-ring", { diameter: size * 1.5, thickness: size * 0.08, tessellation: 18 }, scene);
  body.material = mat(scene, color, true);
  halo.material = mat(scene, color, true);
  body.position.set(f.pos.x, f.pos.y, f.pos.z);
  halo.parent = body;
  halo.rotation.x = Math.PI / 2;
  return {
    gear: { body, halo },
    update(g: { body: Mesh; halo: Mesh }, frame: ProjectileFrame) {
      g.body.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
      g.halo.rotation.z += frame.dt * 14;
      const s = 1 + Math.sin(frame.age * 30) * 0.08;
      g.body.scaling.setAll(s);
    },
    dispose() {
      body.dispose();
      halo.dispose();
    },
  };
}

const hero: ClientHero<Gear> = {
  id: HeroId.IonMan,
  bulk: 1.18,
  shape: { head: 1.05, torso: 1.22, arms: 1.18, legs: 1.08 },
  gear(g) {
    const { scene, rig, add, glow, toon, dark } = g;
    const glowParts: Mesh[] = [];
    const armor = toon("#b8201d");
    const gold = toon("#d99a26");
    const chest = add(MeshBuilder.CreateTorus("arc-reactor", { diameter: 0.38, thickness: 0.055, tessellation: 28 }, scene), rig.torso, glow("#7ef2ff"));
    chest.position.set(0, 0.1, -0.31);
    chest.rotation.x = Math.PI / 2;
    glowParts.push(chest);

    const faceplate = add(MeshBuilder.CreateBox("helmet-faceplate", { width: 0.5, height: 0.34, depth: 0.055 }, scene), rig.head, gold);
    faceplate.position.set(0, 0.03, -0.28);
    const brow = add(MeshBuilder.CreateBox("helmet-brow", { width: 0.58, height: 0.12, depth: 0.08 }, scene), rig.head, armor);
    brow.position.set(0, 0.22, -0.3);
    for (const side of [-1, 1]) {
      const eye = add(MeshBuilder.CreateBox("helmet-eye", { width: 0.16, height: 0.035, depth: 0.03 }, scene), rig.head, glow("#7ef2ff"));
      eye.position.set(side * 0.14, 0.08, -0.325);
    }

    const shoulderL = add(MeshBuilder.CreateBox("left-pauldron", { width: 0.42, height: 0.22, depth: 0.46 }, scene), rig.armL, armor);
    const shoulderR = add(MeshBuilder.CreateBox("right-pauldron", { width: 0.42, height: 0.22, depth: 0.46 }, scene), rig.armR, armor);
    shoulderL.position.set(0, 0.14, 0);
    shoulderR.position.set(0, 0.14, 0);

    for (const side of [-1, 1]) {
      const hand = side < 0 ? rig.handL : rig.handR;
      const gauntlet = add(MeshBuilder.CreateBox("ion-gauntlet", { width: 0.25, height: 0.23, depth: 0.32 }, scene), hand, gold);
      gauntlet.position.set(0, 0, -0.06);
      const palm = add(MeshBuilder.CreateSphere("palm-core", { diameter: 0.14, segments: 12 }, scene), hand, glow("#7ef2ff"));
      palm.position.z = -0.16;
      glowParts.push(palm);
      const boot = add(MeshBuilder.CreateCylinder("boot-thruster", { diameter: 0.2, height: 0.18, tessellation: 12 }, scene), side < 0 ? rig.footL : rig.footR, dark);
      boot.rotation.x = Math.PI / 2;
      boot.position.z = 0.08;
      const flame = add(MeshBuilder.CreateCylinder("boot-flame", { diameterTop: 0.02, diameterBottom: 0.18, height: 0.42, tessellation: 12 }, scene), side < 0 ? rig.footL : rig.footR, glow("#7ef2ff", 0.72));
      flame.rotation.x = Math.PI;
      flame.position.y = -0.22;
      glowParts.push(flame);
    }
    const pack = add(MeshBuilder.CreateBox("back-reactor", { width: 0.8, height: 0.84, depth: 0.22 }, scene), rig.torso, toon("#7b1715"));
    pack.position.set(0, 0.04, 0.36);
    return { chest, beam: cylinderBeam(g), jetFlames: glowParts.slice(3), glowParts, shoulderL, shoulderR };
  },
  animate(gear, f) {
    const over = f.v[V.overdrive] > 0;
    for (const m of gear.glowParts) m.scaling.setAll(over ? 1.22 + Math.sin(f.t * 18) * 0.08 : 1);
    gear.chest.rotation.z = f.t * (over ? 5 : 1.4);

    const activeBeam = f.action === 2;
    const target = f.v[V.beamTarget] > 0 ? f.lookup(f.v[V.beamTarget]) : undefined;
    gear.beam.setEnabled(activeBeam);
    if (activeBeam) {
      const to = target?.center ?? { x: f.eye.x + f.aim.x * 25, y: f.eye.y + f.aim.y * 25, z: f.eye.z + f.aim.z * 25 };
      const dx = to.x - f.eye.x;
      const dy = to.y - f.eye.y;
      const dz = to.z - f.eye.z;
      const len = Math.hypot(dx, dy, dz);
      gear.beam.position.set(f.eye.x + dx * 0.5, f.eye.y + dy * 0.5, f.eye.z + dz * 0.5);
      gear.beam.scaling.set(1, len, 1);
      gear.beam.rotation.y = Math.atan2(dx, dz);
      gear.beam.rotation.x = Math.PI / 2 + Math.asin(dy / (len || 1));
    }
    for (const jet of gear.jetFlames) {
      const on = f.moveKind === 8 || f.v[V.ultGlow] > 0;
      jet.setEnabled(on);
      if (on) jet.scaling.set(1 + Math.sin(f.t * 40) * 0.12, 1 + Math.sin(f.t * 31) * 0.18, 1);
    }
    gear.shoulderL.rotation.z = over ? Math.sin(f.t * 10) * 0.08 : 0;
    gear.shoulderR.rotation.z = over ? -Math.sin(f.t * 10) * 0.08 : 0;
    if (f.moveKind === 8) return "holding-both";
    if (f.action === 1 || f.action === 2) return "holding-both-shoot";
    return "holding-both";
  },
  hud(c, eid) {
    return [
      { label: "Flight", value: hv(c, eid, V.fuel) / 120, color: "#7ef2ff" },
      { label: "Redline", value: hv(c, eid, V.overdrive) / 600, color: "#ffd36a" },
    ];
  },
  lifting(_c, _eid, buttons, grounded) {
    return !grounded || !!(buttons & (Btn.Ability1 | Btn.Jump | Btn.Crouch));
  },
  projectiles: {
    [PK.Repulsor]: (scene, f) => projectile(scene, f, "#7ef2ff", 0.34),
    [PK.Missile]: (scene, f) => projectile(scene, f, "#ffd36a", 0.22),
    [PK.Pulse]: (scene, f) => projectile(scene, f, "#ff8b35", 0.9),
  },
  fx: {
    [FX.Beam]: {
      tracer(c, from, to) {
        const beam = MeshBuilder.CreateCylinder("ion-tracer", { diameter: 0.08, height: 1, tessellation: 12 }, c.scene);
        const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
        const len = Math.hypot(dx, dy, dz);
        beam.position.set(from.x + dx * 0.5, from.y + dy * 0.5, from.z + dz * 0.5);
        beam.scaling.set(1, len, 1);
        beam.rotation.y = Math.atan2(dx, dz);
        beam.rotation.x = Math.PI / 2 + Math.asin(dy / (len || 1));
        beam.material = c.glow("#b9fbff", 0.65);
        c.timed(beam, 0.09, (t, m) => m.scaling.set(1 - t * 0.7, len, 1 - t * 0.7));
      },
      keepGeneric: false,
    },
    [FX.Repulsor]: { keepGeneric: true },
    [FX.Pulse]: {
      explosion(c, pos, radius) {
        const ring = MeshBuilder.CreateTorus("ion-pulse-ring", { diameter: radius * 2, thickness: 0.08, tessellation: 64 }, c.scene);
        ring.position.set(pos.x, pos.y + 0.08, pos.z);
        ring.rotation.x = Math.PI / 2;
        ring.material = c.glow("#ff8b35", 0.7);
        c.timed(ring, 0.75, (t, m) => m.scaling.setAll(0.15 + t * 1.8));
      },
      keepGeneric: true,
    },
  },
  icons: {
    [Slot.Primary]: "M5 12h6m2 0h6M8 8l3 4-3 4M16 8l-3 4 3 4",
    [Slot.Secondary]: "M12 5v14M5 12h14M8 8l8 8M16 8l-8 8",
    [Slot.Ability1]: "M12 3l4 8h-3l2 10-7-12h3z",
    [Slot.Ability2]: "M12 3l7 5-2 9-5 4-5-4-2-9z",
    [Slot.Ability3]: "M4 14c4-6 12-6 16 0M6 17l4-4M14 13l4 4",
    [Slot.Ultimate]: "M12 3a9 9 0 109 9M12 7v10M7 12h10",
    passive: "M6 17l6-14 6 14M9 12h6",
  },
  audio: {
    weight: "mid",
    fire: (ctx) => ({ key: "fire:ion-man", cue: trim(0.1, fire(HeroId.IonMan, ctx)) }),
    reload: () => ionReload,
    ability: {
      [Slot.Ability1]: ionFlight,
      [Slot.Ability2]: ionRedline,
      [Slot.Ability3]: ionMissiles,
      [Slot.Ultimate]: ionPulse,
    },
    theme,
    themeTrim: { heroic: 0.2, hostile: -8, tease: -1.5 },
    loops: ({ v, alive }) => {
      const out = [];
      if (alive && v[V.overdrive] > 0) out.push({ key: "overdrive", build: loops.visor, gain: 0.7 });
      if (alive && v[V.beamTarget] > 0) out.push({ key: "unibeam", build: loops.barrage, gain: 0.45 });
      return out;
    },
    selfLift: ({ alive }) => (alive ? loops.jets : undefined),
    flight: { [PK.Repulsor]: loops.flight("rocket"), [PK.Missile]: loops.flight("missile"), [PK.Pulse]: loops.flight("rocket") },
    explosions: { [FX.Pulse]: (r) => explosion(FX.Pulse, r) },
  },
};

export default hero;
